import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { usePendingApproval, useSaveApproval } from "@/hooks/products/usePendingApproval";
import Fetching from "@/components/Fetching/Fetching";

import ProductPendingHeader from "./ProductPendingHeader";
import SimilarProductItem from "./SimilarProductItem";
import ConfirmNewProduct from "./ConfirmNewProduct";

import styles from "./PendingProductReview.module.scss";

const ProductPendingReview = ({ localeProducts, setLocaleProducts, product }) => {
	const [showAll, setShowAll] = useState(false);
	const suggestions = product?.suggestions || [];
	const visibleCount = 3;

	const { estate_parent_id, estate_id } = useSelector((state) => state.auth.filter);

	const { data } = usePendingApproval({ estate_parent_id, estate_id });

	const { mutate, isLoading: isLoadingSave } = useSaveApproval();

	const [initialProduct, setInitialProduct] = useState(null);
	const [localProduct, setLocalProduct] = useState(null);
	const [allAptitudesLocal, setAllAptitudesLocal] = useState([]);
	const [allActiveIngredientsLocal, setAllActiveIngredientsLocal] = useState([]);
	const [visible, setVisible] = useState(false);
	const [products, setProducts] = useState(false);

	useEffect(() => {
		if (data && data.aptitudes && allAptitudesLocal.length === 0) {
			setAllAptitudesLocal(data.aptitudes);
		}
		if (data && data.active_ingredients && allActiveIngredientsLocal.length === 0) {
			setAllActiveIngredientsLocal(data.active_ingredients);
		}
		if (data && data.products) {
			setProducts(data.products);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	useEffect(() => {
		if (product) {
			setLocalProduct({ ...product, text_aptitude: "", text_active_ingredient: "", reviewed: false });
			setInitialProduct({ ...product, text_aptitude: "", text_active_ingredient: "", reviewed: false });
		}
	}, [product]);

	useEffect(() => {
		if (localProduct && localProduct.reviewed) {
			setTimeout(() => setVisible(false), 300);
		} else {
			setVisible(true);
		}
	}, [localProduct]);

	const handleConfirmProduct = () => {
		mutate(
			{ ...localProduct, filters: { estate_parent_id, estate_id } },
			{
				onSuccess: (_response) => {
					toast.success("Producto confirmado correctamente");

					if (localeProducts.length === 1) {
						window.location.reload();
					}

					setLocaleProducts((prevProducts) => {
						return prevProducts.filter((p) => p.value !== product.value);
					});
				},
			},
		);
	};

	const changeProduct = (suggestion) => {
		let newProduct = { ...localProduct };

		newProduct.text_product = suggestion.label;
		newProduct.value = suggestion.value;
		newProduct.label = suggestion.label;
		newProduct.aptitudes = allAptitudesLocal.filter((aptitude) => suggestion.aptitudes.includes(aptitude.value));
		newProduct.active_ingredients = allActiveIngredientsLocal.filter((ingredient) => suggestion.active_ingredients.includes(ingredient.value));
		newProduct.reviewed = true;

		setLocalProduct(newProduct);
	};

	const cancelProduct = () => {
		setLocalProduct(initialProduct);
	};

	const visibleSuggestions = showAll ? suggestions : suggestions.slice(0, visibleCount);
	const hiddenCount = suggestions.length - visibleCount;

	if (!localProduct || isLoadingSave) {
		return <Fetching bottomMargin={"2em"} topMargin={"2em"} />;
	}

	return (
		<div className={styles.reviewCard}>
			<ProductPendingHeader localProduct={localProduct} />

			{!!data?.has_permission && (
				<>
					{visible && (
						<div className={`${styles.reviewContainer} ${localProduct.reviewed ? styles.slideUpOut : styles.slideUpIn}`}>
							<div className={styles.infoAlert}>
								<span className={styles.infoIcon}>?</span>
								<strong>¿Este producto ya existe? </strong> <span>Revisa los productos similares encontrados.</span>
							</div>

							<div className={styles.foundAlert}>
								<span className={styles.count}>{suggestions.length}</span> Productos similares encontrados:
							</div>

							<div className={styles.similarList}>
								{visibleSuggestions.map((item, idx) => (
									<SimilarProductItem
										key={idx}
										item={item}
										allAptitudesLocal={allAptitudesLocal}
										allActiveIngredientsLocal={allActiveIngredientsLocal}
										changeProduct={changeProduct}
									/>
								))}
								{!showAll && hiddenCount > 0 && (
									<div className={styles.viewMore} onClick={() => setShowAll(true)} style={{ cursor: "pointer" }}>
										Ver {hiddenCount} más
									</div>
								)}
							</div>
						</div>
					)}

					<ConfirmNewProduct
						localProduct={localProduct}
						allAptitudesLocal={allAptitudesLocal}
						allActiveIngredientsLocal={allActiveIngredientsLocal}
						setLocalProduct={setLocalProduct}
						setAllAptitudesLocal={setAllAptitudesLocal}
						setAllActiveIngredientsLocal={setAllActiveIngredientsLocal}
						reviewed={localProduct.reviewed}
						cancelProduct={cancelProduct}
						handleConfirmProduct={handleConfirmProduct}
						products={products}
					/>
				</>
			)}
		</div>
	);
};

export default ProductPendingReview;
